import { useState, useRef, useEffect } from "react";
import { useCompany } from "@/contexts/CompanyContext";
import { BuildingsIcon, CaretDownIcon, CheckIcon, MapPinIcon } from "@phosphor-icons/react";

export default function CompanySwitcher() {
  const { companies, branches, current, currentBranch, companyId, branchId, switchCompany, switchBranch } = useCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!current) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="company-switcher"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 min-h-[44px] px-2 sm:px-3 py-1.5 bg-card border border-border rounded-md hover:border-agro-green/40 transition-all group"
        aria-label="Cambiar empresa o sucursal"
      >
        <div className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-black text-xs" style={{ backgroundColor: current.color || "#02BF44" }}>
          {current.codigo.slice(0, 2)}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-[10px] uppercase tracking-wider text-text-muted leading-none">Empresa</div>
          <div className="text-sm font-medium text-text-primary leading-tight mt-0.5" data-testid="current-branch-label">
            {current.nombre}
            {currentBranch ? <span className="text-text-muted ml-1">· {currentBranch.nombre}</span>
              : branches.length > 1 && <span className="text-agro-yellow ml-1">· Elige sucursal</span>}
          </div>
        </div>
        <CaretDownIcon size={14} className={`text-text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] bg-card border border-border rounded-lg shadow-xl shadow-black/40 z-50 overflow-hidden" data-testid="company-dropdown">
          <div className="p-3 border-b border-border bg-sidebar/60">
            <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-medium">Empresas</div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {companies.map((c) => (
              <button
                key={c.id}
                onClick={() => c.id !== companyId && switchCompany(c.id)}
                data-testid={`switch-company-${c.codigo}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.03] transition-colors ${c.id === companyId ? "bg-agro-green/5" : ""}`}
              >
                <div className="w-9 h-9 rounded-md flex items-center justify-center font-bold text-black text-xs flex-shrink-0" style={{ backgroundColor: c.color || "#02BF44" }}>
                  {c.codigo.slice(0, 2)}
                </div>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary truncate">{c.nombre}</div>
                  <div className="text-[11px] text-text-muted truncate">RTN {c.rtn || "—"}</div>
                </div>
                {c.id === companyId && <CheckIcon size={16} weight="bold" className="text-agro-green" />}
              </button>
            ))}
          </div>

          {branches.length > 0 && (
            <>
              <div className="p-3 border-y border-border bg-sidebar/60 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.2em] text-text-muted font-medium flex items-center gap-1">
                  <MapPinIcon size={11} /> Sucursal emisora
                </div>
                <span className="text-[10px] text-text-muted">{branches.length}</span>
              </div>
              <div className="px-3 py-1.5 text-[10px] text-text-muted border-b border-border/40">
                Los documentos que emitas usan la dirección, la autorización y la numeración de esta sucursal.
              </div>
              <div className="max-h-48 overflow-y-auto">
                {branches.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => switchBranch(b.id)}
                    data-testid={`switch-branch-${b.codigo}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] ${b.id === branchId ? "bg-agro-green/5" : ""}`}
                  >
                    <div className="w-7 h-7 rounded-md flex items-center justify-center bg-agro-yellow/10 text-agro-yellow text-[10px] font-mono font-bold flex-shrink-0">
                      {b.codigo}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm text-text-primary">{b.nombre}</div>
                      <div className="text-[11px] text-text-muted">{b.manager || "—"}</div>
                    </div>
                    {b.id === branchId && <CheckIcon size={14} weight="bold" className="text-agro-green" />}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="p-2 border-t border-border bg-sidebar/40">
            <a href="/empresas" className="block w-full text-center text-xs text-agro-green hover:underline py-1.5">
              Administrar empresas y sucursales
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
